/**
 * DevizLibrary - Component for displaying the list of saved estimates
 * Extracted from DevizTab.jsx for better maintainability
 */
import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { FolderOpen, PlusCircle, FileSpreadsheet, Eye, Edit, Trash2, Copy, FileSignature, Search, X, Hash, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper for formatting numbers
const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "0,00";
  return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

export function DevizLibrary({
  devizeList = [],
  loadingDevize = false,
  companyId,
  onNewDeviz,
  onEditDeviz,
  onViewDeviz,
  onDeleteDeviz,
  onDuplicateDeviz,
  onGenerateContract,
  HelpTooltip
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showNumbering, setShowNumbering] = useState(false);

  const filteredDevize = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return devizeList;
    return devizeList.filter((d) => {
      const num = (d.numar_deviz || d.numarDeviz || "").toLowerCase();
      const ben = (d.beneficiar || "").toLowerCase();
      const obj = (d.obiectiv || "").toLowerCase();
      const status = (d.status === "final" ? "final" : "ciornă ciorna").toLowerCase();
      const total = String(d.totals?.totalCuTVA ?? "").toLowerCase();
      const dateStr = d.created_at ? new Date(d.created_at).toLocaleDateString("ro-RO") : "";
      return (
        num.includes(q) ||
        ben.includes(q) ||
        obj.includes(q) ||
        status.includes(q) ||
        total.includes(q) ||
        dateStr.includes(q)
      );
    });
  }, [devizeList, searchTerm]);

  return (
    <Card className="border-0 shadow-lg overflow-hidden" data-testid="deviz-library">
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="min-w-0 max-w-full">
            <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              <FolderOpen className="w-5 h-5 text-green-600 shrink-0" />
              Bibliotecă Devize
              {HelpTooltip && <HelpTooltip section="deviz" />}
            </CardTitle>
            <CardDescription className="text-base mt-1">
              {searchTerm
                ? `${filteredDevize.length} din ${devizeList.length} devize`
                : `${devizeList.length} devize salvate`}
            </CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              onClick={() => setShowNumbering(true)}
              variant="outline"
              className="w-full sm:w-auto border-slate-300"
              data-testid="deviz-numbering-settings-btn"
            >
              <Hash className="w-4 h-4 mr-2" />
              Editare nr. deviz curent
            </Button>
            <Button onClick={onNewDeviz} className="bg-green-600 hover:bg-green-700 w-full sm:w-auto" data-testid="new-deviz-btn">
              <PlusCircle className="w-4 h-4 mr-2" />
              Deviz Nou
            </Button>
          </div>
        </div>
        {devizeList.length > 0 && (
          <div className="mt-4 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="text"
              placeholder="Caută după număr, beneficiar, obiectiv, status sau total…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-9 h-10 border-slate-200 focus-visible:ring-green-500"
              data-testid="deviz-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                aria-label="Șterge căutarea"
                data-testid="deviz-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="px-3 sm:px-6 overflow-hidden">
        {loadingDevize ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : devizeList.length === 0 ? (
          <EmptyState onNewDeviz={onNewDeviz} />
        ) : filteredDevize.length === 0 ? (
          <NoResultsState searchTerm={searchTerm} onClear={() => setSearchTerm("")} />
        ) : (
          <DevizList 
            devizeList={filteredDevize}
            onEditDeviz={onEditDeviz}
            onViewDeviz={onViewDeviz}
            onDeleteDeviz={onDeleteDeviz}
            onDuplicateDeviz={onDuplicateDeviz}
            onGenerateContract={onGenerateContract}
          />
        )}
      </CardContent>
      <NumberingSettingsDialog
        open={showNumbering}
        onOpenChange={setShowNumbering}
        companyId={companyId}
      />
    </Card>
  );
}

function NumberingSettingsDialog({ open, onOpenChange, companyId }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState("");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!open || !companyId) return;
    setLoading(true);
    axios
      .get(`${API}/devize/settings/numbering`, { params: { company_id: companyId } })
      .then((r) => {
        setInfo(r.data);
        setValue(String(r.data.current_number ?? 0));
      })
      .catch(() => toast.error("Nu s-au putut încărca setările de numerotare"))
      .finally(() => setLoading(false));
  }, [open, companyId]);

  const parsed = parseInt(value, 10);
  const valid = !isNaN(parsed) && parsed >= 0;
  const nextNum = valid ? parsed + 1 : 0;
  const nextLabel = valid ? `DEV${String(nextNum).padStart(4, "0")}` : "—";
  const existingMax = info?.existing_max ?? 0;
  const belowMin = valid && parsed < existingMax;

  const handleSave = async () => {
    if (!valid || belowMin) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/devize/settings/numbering?company_id=${companyId}`,
        { current_number: parsed }
      );
      toast.success(`Numerotare salvată. Următorul deviz: ${res.data.next_label}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvarea numerotării");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="deviz-numbering-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-green-600" />
            Editare număr deviz curent
          </DialogTitle>
          <DialogDescription>
            Setează numărul de la care să continue devizele. Util când preiei o serie
            dintr-un alt program — introdu ultimul număr folosit acolo.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="deviz-current-number">Ultimul număr de deviz folosit</Label>
              <Input
                id="deviz-current-number"
                type="number"
                min={existingMax}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="focus-visible:ring-green-500"
                data-testid="deviz-numbering-input"
              />
              {belowMin && (
                <p className="text-xs text-red-500" data-testid="deviz-numbering-error">
                  Nu poți seta un număr mai mic decât ultimul deviz existent ({existingMax}).
                </p>
              )}
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Următorul deviz creat va fi</p>
              <p className="text-lg font-bold text-green-700" data-testid="deviz-numbering-preview">
                {nextLabel}
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="deviz-numbering-cancel">
            Anulează
          </Button>
          <Button
            onClick={handleSave}
            disabled={!valid || belowMin || saving || loading}
            className="bg-green-600 hover:bg-green-700"
            data-testid="deviz-numbering-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NoResultsState({ searchTerm, onClear }) {
  return (
    <div className="text-center py-12 bg-slate-50 rounded-lg" data-testid="deviz-no-results">
      <Search className="w-10 h-10 mx-auto text-slate-400 mb-3" />
      <h3 className="text-base font-medium text-slate-600 mb-1">Niciun deviz găsit</h3>
      <p className="text-sm text-slate-500 mb-4">
        Nu există devize care conțin <strong>&ldquo;{searchTerm}&rdquo;</strong>.
      </p>
      <Button variant="outline" size="sm" onClick={onClear} data-testid="deviz-clear-search-btn">
        Șterge filtrul
      </Button>
    </div>
  );
}

function EmptyState({ onNewDeviz }) {
  return (
    <div className="text-center py-12 bg-slate-50 rounded-lg" data-testid="deviz-empty-state">
      <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-4" />
      <h3 className="text-lg font-medium text-slate-600 mb-2">Nu există devize</h3>
      <p className="text-slate-500 mb-4">Creează primul deviz pentru a începe</p>
      <Button onClick={onNewDeviz} className="bg-green-600 hover:bg-green-700">
        <PlusCircle className="w-4 h-4 mr-2" />
        Creează Deviz
      </Button>
    </div>
  );
}

function DevizList({ devizeList, onEditDeviz, onViewDeviz, onDeleteDeviz, onDuplicateDeviz, onGenerateContract }) {
  return (
    <div className="space-y-3" data-testid="deviz-list">
      {devizeList.map((deviz) => (
        <DevizCard 
          key={deviz.id}
          deviz={deviz}
          onEdit={() => onEditDeviz(deviz)}
          onView={() => onViewDeviz(deviz)}
          onDelete={() => onDeleteDeviz(deviz.id)}
          onDuplicate={() => onDuplicateDeviz(deviz)}
          onGenerateContract={() => onGenerateContract?.(deviz)}
        />
      ))}
    </div>
  );
}

function DevizCard({ deviz, onEdit, onView, onDelete, onDuplicate, onGenerateContract }) {
  return (
    <div 
      className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors gap-3 w-full max-w-full overflow-hidden"
      data-testid={`deviz-card-${deviz.id}`}
    >
      <div 
        className="flex-1 min-w-0 w-full cursor-pointer hover:opacity-80 transition-opacity"
        onClick={onEdit}
        title="Click pentru editare"
      >
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-bold text-green-700 hover:text-green-800 hover:underline break-all">
            {deviz.numar_deviz || deviz.numarDeviz}
          </span>
          <Badge variant={deviz.status === 'final' ? 'default' : 'secondary'} className="text-xs">
            {deviz.status === 'final' ? 'Final' : 'Ciornă'}
          </Badge>
          {deviz.contract_generated && (
            <Badge className="bg-indigo-100 text-indigo-700 text-xs">Contract</Badge>
          )}
        </div>
        <p className="text-xs sm:text-sm text-slate-600 break-words line-clamp-2">
          {deviz.beneficiar || 'Beneficiar nespecificat'} • {deviz.obiectiv || 'Fără obiectiv'}
        </p>
        <p className="text-[10px] sm:text-xs text-slate-400 mt-1 break-words">
          Creat: {new Date(deviz.created_at).toLocaleDateString('ro-RO')} • 
          Total: {formatNumber(deviz.totals?.totalCuTVA || 0)} RON
        </p>
      </div>
      <div className="flex gap-1 sm:gap-2 flex-wrap justify-start sm:justify-end shrink-0 w-full sm:w-auto">
        {deviz.status === 'final' && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onGenerateContract} 
            className="bg-indigo-50 border-indigo-200 text-indigo-600 hover:bg-indigo-100 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 sm:px-3 gap-0 sm:gap-1" 
            title="Generează Contract din Deviz"
            data-testid={`generate-contract-${deviz.id}`}
          >
            <FileSignature className="w-4 h-4" />
            <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Contract</span>
            <span className="hidden sm:inline ml-1">Contract</span>
          </Button>
        )}
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onDuplicate} 
          className="bg-green-50 border-green-200 text-green-600 hover:bg-green-100 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 gap-0" 
          title="Generează o copie a devizului"
          data-testid={`duplicate-deviz-${deviz.id}`}
        >
          <Copy className="w-4 h-4" />
          <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Copie</span>
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onView} 
          className="bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 gap-0" 
          title="Previzualizare deviz"
          data-testid={`view-deviz-${deviz.id}`}
        >
          <Eye className="w-4 h-4" />
          <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Vezi</span>
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onEdit} 
          className="bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-100 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 gap-0" 
          title="Editare deviz"
          data-testid={`edit-deviz-${deviz.id}`}
        >
          <Edit className="w-4 h-4" />
          <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Editare</span>
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="bg-red-50 border-red-200 text-red-500 hover:bg-red-100 hover:text-red-700 flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 gap-0" 
          onClick={onDelete} 
          title="Șterge deviz"
          data-testid={`delete-deviz-${deviz.id}`}
        >
          <Trash2 className="w-4 h-4" />
          <span className="text-[9px] leading-tight mt-0.5 sm:hidden">Șterge</span>
        </Button>
      </div>
    </div>
  );
}

export default DevizLibrary;
